import React, { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { getNodeShortLabel, getNodeSubtitle, getNodeDescription } from '../utils/nodeCriteria'
import { getNodeDef, getNodePhase, PHASE_INTUITION, PHASE_PRECISION, PHASE_MAITRISE } from '../data/parcoursTree'
import { getImageUrlAndCrop } from '../data/parcoursIllustrations'
import { useParcoursImages } from '../contexts/ParcoursImagesContext'
import { getExercisesForNode, getExercisesListForNode, getExerciseDominantMode, PARCOURS_LAST_MODE_KEY, getParcoursLastExerciseId, setParcoursLastExerciseId, getVideoRanksForExercises } from '../services/exerciseService'
import { getNextPreferredDifficulty } from '../utils/parcoursDifficultySequence'
import { getUserProgress } from '../services/progressionService'
import { getCodexEntriesForNode } from '../utils/codexHelpers'
import { getCodexUrlForEntry } from '../utils/codexContextLinks'
import ExerciseCard from './ExerciseCard'
import './ParcoursNodePage.css'

function getMasteryLevelLabel(masteryLevel) {
  switch (masteryLevel) {
    case 'beginner': return 'Débutant'
    case 'intermediate': return 'Intermédiaire'
    case 'advanced': return 'Avancé'
    default: return 'Débutant'
  }
}

function getProgressionModeLabel(progressionMode) {
  switch (progressionMode) {
    case 'functions': return 'Fonctions'
    case 'qcm': return 'QCM'
    case 'full': return 'Complet'
    default: return 'Fonctions'
  }
}

function ParcoursNodePage({
  nodeId,
  userId,
  progress,
  doneExerciseIds = [],
  onBack,
  onOpenCodex,
  isPreviewMode = false,
  previewProgress = null,
}) {
  const navigate = useNavigate()
  const { imageEntries } = useParcoursImages()
  const [exerciseList, setExerciseList] = useState([])
  const [loadingList, setLoadingList] = useState(true)
  const [loadingRandom, setLoadingRandom] = useState(false)
  const [localProgress, setLocalProgress] = useState(null)
  const [videoRanksMap, setVideoRanksMap] = useState(() => new Map())

  const effectiveProgress = progress ?? previewProgress ?? localProgress

  useEffect(() => {
    if (progress != null || previewProgress != null || !userId) return
    let cancelled = false
    getUserProgress(userId)
      .then((data) => { if (!cancelled) setLocalProgress(data) })
      .catch(() => { if (!cancelled) setLocalProgress(null) })
    return () => { cancelled = true }
  }, [userId, progress, previewProgress])

  const nodeDef = getNodeDef(nodeId)
  const isUnlocked = effectiveProgress?.unlockedNodes?.includes(nodeId)
  const nodeStats = effectiveProgress?.nodeStats?.[nodeId]

  const getEffectiveProgressionMode = () => {
    if (!nodeStats) return 'functions'
    const phase = getNodePhase(nodeId, nodeStats)
    if (phase === PHASE_INTUITION) return 'functions'
    if (phase === PHASE_PRECISION) return 'qcm'
    return 'full'
  }

  const doneSet = useMemo(() => new Set(doneExerciseIds || []), [doneExerciseIds])
  const orderedExercises = useMemo(() => {
    if (!exerciseList.length) return []
    const notDone = exerciseList.filter(ex => !doneSet.has(ex.id))
    const done = exerciseList.filter(ex => doneSet.has(ex.id))
    return [...notDone, ...done]
  }, [exerciseList, doneSet])

  useEffect(() => {
    if (!orderedExercises.length) {
      setVideoRanksMap(new Map())
      return
    }
    getVideoRanksForExercises(orderedExercises).then(setVideoRanksMap).catch(() => setVideoRanksMap(new Map()))
  }, [orderedExercises])

  const allDone = exerciseList.length > 0 && orderedExercises.length > 0 && doneSet.size > 0 && exerciseList.every(ex => doneSet.has(ex.id))

  useEffect(() => {
    let cancelled = false
    setLoadingList(true)
    getExercisesListForNode(nodeId)
      .then((list) => {
        if (!cancelled) setExerciseList(list || [])
      })
      .catch(() => {
        if (!cancelled) setExerciseList([])
      })
      .finally(() => {
        if (!cancelled) setLoadingList(false)
      })
    return () => { cancelled = true }
  }, [nodeId])

  const rawEntry = imageEntries[nodeId] ?? imageEntries['1.1']
  const { url: imageUrl } = getImageUrlAndCrop(rawEntry)
  const bgPosition = nodeId === '4.1' ? '50% 30%' : 'center'
  let watermarkStyle = {}
  if (imageUrl) {
    watermarkStyle = {
      backgroundImage: `linear-gradient(180deg, rgba(255,255,255,0.85) 0%, rgba(255,255,255,0.81) 100%), url(${imageUrl})`,
      backgroundSize: 'cover',
      backgroundPosition: bgPosition,
      backgroundRepeat: 'no-repeat',
    }
  }

  const handleLaunchRandom = async () => {
    if (!isUnlocked) return
    setLoadingRandom(true)
    try {
      const lastMode = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(PARCOURS_LAST_MODE_KEY) : null
      const preferredDifficulty = getNextPreferredDifficulty()
      const exercise = await getExercisesForNode(nodeId, {
        lastMode: lastMode || undefined,
        doneExerciseIds: doneExerciseIds || [],
        preferredDifficulty,
        excludeExerciseId: getParcoursLastExerciseId(nodeId),
      })
      if (exercise?.id) {
        const dominantMode = getExerciseDominantMode(exercise)
        if (typeof sessionStorage !== 'undefined' && dominantMode) {
          sessionStorage.setItem(PARCOURS_LAST_MODE_KEY, dominantMode)
        }
        setParcoursLastExerciseId(nodeId, exercise.id)
        navigate(`/play/${exercise.id}?mode=parcours&node=${nodeId}`, { state: { returnTo: `/student-dashboard?tab=campaign&node=${nodeId}` } })
      }
    } catch (err) {
      console.error('Erreur lancement exercice aléatoire:', err)
    } finally {
      setLoadingRandom(false)
    }
  }

  const codexEntries = useMemo(() => getCodexEntriesForNode(nodeId), [nodeId])

  if (!nodeId || !nodeDef) {
    return (
      <div className="parcours-node-page">
        <p>Nœud inconnu.</p>
        <button type="button" className="parcours-node-back" onClick={onBack}>Retour</button>
      </div>
    )
  }

  return (
    <div className="parcours-node-page" style={watermarkStyle}>
      <div className="parcours-node-page-inner">
        <button
          type="button"
          className="parcours-node-back"
          onClick={onBack}
          aria-label="Retour au parcours"
        >
          ← Retour
        </button>

        <header className="parcours-node-header">
          <h1 className="parcours-node-title">
            {getNodeShortLabel(nodeId)} — {getNodeSubtitle(nodeId)}
          </h1>
          <p className="parcours-node-objective" role="doc-subtitle">
            {getNodeDescription(nodeId)}
          </p>
          {isUnlocked && nodeStats && (
            <div className="parcours-node-stats">
              <span>Mode : {getProgressionModeLabel(getEffectiveProgressionMode())}</span>
              <span>Score moyen : {Math.round(nodeStats.averageScore ?? 0)}%</span>
              <span>Niveau : {getMasteryLevelLabel(nodeStats.masteryLevel)}</span>
            </div>
          )}
        </header>

        {isUnlocked && (
          <>
            <section className="parcours-node-actions" aria-labelledby="parcours-node-actions-heading">
              <h2 id="parcours-node-actions-heading" className="parcours-node-section-title">Pratiquer</h2>
              <button
                type="button"
                className="parcours-node-btn-random"
                onClick={handleLaunchRandom}
                disabled={loadingRandom || loadingList}
                aria-label="Lancer un exercice au hasard pour ce niveau"
              >
                {loadingRandom ? 'Chargement…' : 'Un exercice au hasard'}
              </button>
              {allDone && (
                <p className="parcours-node-all-done-msg">
                  Tous les exercices de ce niveau ont déjà été pratiqués. Tu peux en refaire un pour réviser ou passer au niveau suivant.
                </p>
              )}
            </section>

            <section className="parcours-node-list" aria-labelledby="parcours-node-list-heading">
              <h2 id="parcours-node-list-heading" className="parcours-node-section-title">Choisir un exercice</h2>
              {loadingList ? (
                <div className="parcours-node-loading">
                  <span className="spinner-small" aria-hidden /> Chargement…
                </div>
              ) : orderedExercises.length === 0 ? (
                <p className="parcours-node-empty">Aucun exercice disponible pour ce niveau pour l&apos;instant.</p>
              ) : (
                <div className="parcours-node-exercise-grid">
                  {orderedExercises.map((ex) => {
                    const isDone = doneSet.has(ex.id)
                    const vr = videoRanksMap.get(ex.id)
                    return (
                      <ExerciseCard
                        key={ex.id}
                        exercise={ex}
                        allExercises={orderedExercises}
                        videoRank={vr?.rank}
                        videoGroupSize={vr?.groupSize}
                        variant="mini"
                        onClick={(exerciseId) => {
                          setParcoursLastExerciseId(nodeId, exerciseId)
                          navigate(`/play/${exerciseId}?mode=parcours&node=${nodeId}`, { state: { returnTo: `/student-dashboard?tab=campaign&node=${nodeId}` } })
                        }}
                        onOpenCodex={(ficheId, sectionId) => navigate(getCodexUrlForEntry(ficheId, sectionId))}
                        recommendedLabel={isDone ? 'Déjà fait' : undefined}
                      />
                    )
                  })}
                </div>
              )}
            </section>
          </>
        )}

        {codexEntries.length > 0 && (
          <section className="parcours-node-codex" aria-labelledby="parcours-node-codex-heading">
            <h2 id="parcours-node-codex-heading" className="parcours-node-section-title">Pour aller plus loin</h2>
            <button
              type="button"
              className="parcours-node-btn-codex"
              onClick={() => onOpenCodex?.(nodeId)}
              aria-label="Voir le cours (Codex) pour ce niveau"
            >
              Voir le cours (Codex)
            </button>
          </section>
        )}
      </div>
    </div>
  )
}

export default ParcoursNodePage
